import { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';
import { useParams } from 'react-router';
import { Chip } from '@mui/material';

// Hooks and services
import { getLucTransfersReceiptById } from '../../../services/luc-transfer.service';
import { makeLucTransferReceiptItemColumns } from '../../../utils/columns/makeLucTransferReceiptItemColumns';
import { useNewTable, Table } from '../../common/table';

// Types
import { LUCTransferReceipt, LUCTransferReceiptItem } from '../../../types/luc-transfer-receipt';
import { LUCTransferStatus, LUCTransferType } from '../../../types/luc-transfer';

// Utils
import useDateTimeFormat from '../../../hooks/useDateTimeFormat';

// Components
import { Spinner } from '../../common/Spinner';
import { OverviewCard, OverviewGrid, GridField } from '../../common/OverviewCard/OverviewCard';

const val = <T,>(v: T | undefined | null): T | string => (v == null || v === '' ? '-' : v);

export function TransferDetails() {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  const { transferId } = useParams();
  const [receipt, setReceipt] = useState<LUCTransferReceipt | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const columns = useMemo(() => makeLucTransferReceiptItemColumns(), []);

  const table = useNewTable<LUCTransferReceiptItem>({
    columns: columns,
    serverSide: false,
  });

  useEffect(() => {
    if (transferId) fetchTransfer(transferId);
  }, [transferId]);

  async function fetchTransfer(id: string) {
    try {
      setIsLoading(true);
      setReceipt(await getLucTransfersReceiptById(id));
    } catch {
      setReceipt(null);
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) return <Spinner loading={true} />;

  if (!receipt) return <div>Could not find transfer receipt record with this ID.</div>;

  // ---- Flattened field list (no section headings) ---------------------------
  const fields: GridField[] = [
    { label: 'Transfer Type', value: val((LUCTransferType[receipt.type] ?? '-').toString()) },
    { label: withTimeMode('Transfer Timestamp'), value: val(receipt.timestamp ? toStandardDate(receipt.timestamp) : null) },
    { label: 'Quantity', value: val(receipt.itemCount) },

    { label: 'Receipt Id', value: val(receipt.id) },
    { label: 'Session Id', value: val(receipt.lucTransferId) },
    { label: 'Control Receipt No.', value: val(receipt.controlReceiptNr) },
    { label: withTimeMode('Created'), value: val(toStandardDate(receipt.createdDateTime)) },
    { label: 'User Name', value: val(receipt.userName) },

    { label: 'Transferring Party', value: val(receipt.transferringPartyName) },
    { label: 'Transfer Station', value: val(receipt.transferringFromStationIataCode) },
    {
      label: 'Transferring Party Name',
      value: val(receipt.signatures?.length > 0 ? `${receipt.signatures[0].name} (${receipt.signatures[0].email})` : null),
      valueClassName: 'overview-value--wrap',
    },
    { label: 'Facility Name', value: val(receipt.facilityName) },

    { label: 'Receiving Party', value: val(receipt.receivingPartyName) },
    { label: 'Next Station', value: val(receipt.transferringToStationIataCode) },
    {
      label: 'Receiving Party Name',
      value: val(
        receipt.signatures?.length > 1
          ? receipt.signatures
              .slice(1)
              .map((sig) => `${sig.name} (${sig.email})`)
              .join(', ')
          : null,
      ),
      valueClassName: 'overview-value--wrap',
    },
  ];

  // ---- Chunk into rows of 4 --------------------------------------------------
  const rows: GridField[][] = [];
  for (let i = 0; i < fields.length; i += 4) {
    rows.push(fields.slice(i, i + 4));
  }

  return (
    <>
      <OverviewCard
        title='Transfer Summary'
        headerRight={receipt.status === LUCTransferStatus.Draft ? <Chip label='Draft' color='warning' size='small' variant='outlined' /> : undefined}
      >
        <OverviewGrid columns={4} rows={rows} />
      </OverviewCard>

      <div style={{ marginTop: 16 }}>
        <Table
          getCoreRowModel={getCoreRowModel()}
          {...table}
          data={receipt.items}
          showAction={false}
          showColumnToggler={false}
          showSearchBar={true}
          minRows={5}
          showPagination={false}
        />
      </div>
    </>
  );
}
